/**
 * ThemeService.ts
 * Centralized theme and color scheme management for SkySkout Desktop UI.
 * Supports 4 unified core themes:
 * - dark: SkySkout (Dark) (Baseline Obsidian Glass Default)
 * - earth: Earth (PNW Naturalist / Earth & River)
 * - paper: Paper (Cartography / Royal Blue & Terracotta)
 * - forest: Forest (GaiaGPS Outdoor Field Scout)
 */

export type ThemeId = 'dark' | 'earth' | 'scifi' | 'paper' | 'forest';

export interface ThemeMeta {
  id: ThemeId;
  name: string;
  category: string;
  icon: string;
  accentColor: string;
  bgPreview: string;
  description: string;
}

export const THEMES_LIST: ThemeMeta[] = [
  {
    id: 'dark',
    name: 'SkySkout (Dark)',
    category: 'Standard',
    icon: '🌑',
    accentColor: '#00d4ff',
    bgPreview: '#030303',
    description: 'Obsidian glass canvas with vivid cyan telemetry vectors. Default field dark mode.'
  },
  {
    id: 'earth',
    name: 'Earth',
    category: 'Naturalist',
    icon: '🌿',
    accentColor: '#86a37c',
    bgPreview: '#2e4542',
    description: 'Natural earth palette: deep spruce slate, forest moss, lichen sage, and warm canyon sandstone.'
  },
  {
    id: 'scifi',
    name: 'Sci-Fi',
    category: 'Futuristic',
    icon: '🛸',
    accentColor: '#38bdf8',
    bgPreview: '#091117',
    description: 'Deep neon nebula slate with cosmic cyan and plasma teal telemetry vectors.'
  },
  {
    id: 'paper',
    name: 'Paper',
    category: 'Cartography',
    icon: '🧭',
    accentColor: '#1d4ed8',
    bgPreview: '#ded0ba',
    description: 'Tactile topographic map sepia, sharp dark ink borders, and drafting ultramarine accents.'
  },
  {
    id: 'forest',
    name: 'Forest',
    category: 'Outdoor',
    icon: '🌲',
    accentColor: '#22c55e',
    bgPreview: '#07130b',
    description: 'Deep alpine evergreen canopy, Gaia trail green accents, and compact utilitarian controls.'
  }
];

class ThemeService {
  private static instance: ThemeService;
  private currentTheme: ThemeId = 'dark';
  private listeners: Set<(theme: ThemeId) => void> = new Set();

  public static getInstance(): ThemeService {
    if (!ThemeService.instance) {
      ThemeService.instance = new ThemeService();
    }
    return ThemeService.instance;
  }

  constructor() {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('skyskout_theme');
      if (saved && THEMES_LIST.some(t => t.id === saved)) {
        this.currentTheme = saved as ThemeId;
      } else if (saved === 'light') {
        this.currentTheme = 'paper';
      } else if (saved === 'emerald') {
        this.currentTheme = 'forest';
      } else {
        this.currentTheme = 'dark';
      }
      this.applyTheme(this.currentTheme);
    }
  }

  public getTheme(): ThemeId {
    return this.currentTheme;
  }

  public isLightPalette(themeId?: ThemeId): boolean {
    const id = themeId || this.currentTheme;
    return id === 'paper';
  }

  public getThemeMeta(themeId?: ThemeId): ThemeMeta {
    const id = themeId || this.currentTheme;
    return THEMES_LIST.find(t => t.id === id) || THEMES_LIST[0];
  }

  public getAvailableThemes(): ThemeMeta[] {
    return THEMES_LIST;
  }

  public setTheme(theme: ThemeId): void {
    if (theme === this.currentTheme) return;
    this.currentTheme = theme;
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('skyskout_theme', theme);
      } catch (_) {}
      this.applyTheme(theme);
    }
    this.notifyListeners();
  }

  private applyTheme(theme: ThemeId): void {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', theme);
      // Ensure body receives theme attribute as well for high-specificity selectors
      document.body.setAttribute('data-theme', theme);
    }
  }

  public subscribe(listener: (theme: ThemeId) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notifyListeners(): void {
    for (const listener of this.listeners) {
      try {
        listener(this.currentTheme);
      } catch (err) {
        console.error('Error in ThemeService listener:', err);
      }
    }
  }
}

export const themeService = ThemeService.getInstance();
export default themeService;
